import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  CalendarDays,
  Video,
  BookOpen,
  Star,
  Search,
  GraduationCap,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import {
  EXAM_HUB_DATA,
  getRegisteredSubjectValues,
  getUecSubjectValue,
} from "../../Data/Dashboard/ExamHub/ExamHubData";
import type { ExamType } from "../../types/examHub";
import SessionCard from "../../Components/Dashboard/ExamHubPage/SessionCard";
import ResourceCard from "../../Components/Dashboard/ExamHubPage/ResourceCard";
import { useAuth } from "../../context/AuthContext";
import { app } from "../../firebase";

type Tab = "schedule" | "resources";

const toSpmSubjectKey = (subject: unknown) =>
  String(subject || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const functions = getFunctions(app, "asia-southeast1");
const getOrCreateReferralCode = httpsCallable(
  functions,
  "getOrCreateReferralCode",
);

export default function ExamHubPage() {
  const { user } = useAuth();
  const [examType, setExamType] = useState<ExamType>("UEC");
  const [tab, setTab] = useState<Tab>("schedule");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [noteSearch, setNoteSearch] = useState("");
  const [noteTypeFilter, setNoteTypeFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState<
    "all" | "upcoming" | "completed" | "recordings"
  >("all");
  const [referralCode, setReferralCode] = useState("");
  const [isReferralLoading, setIsReferralLoading] = useState(true);
  const [referralMessage, setReferralMessage] = useState("");

  useEffect(() => {
    let active = true;

    getOrCreateReferralCode()
      .then((result) => {
        const code = String((result.data as { code?: string })?.code || "");
        if (active) setReferralCode(code);
      })
      .catch((error) => {
        console.error("Failed to load referral code:", error);
        if (active) {
          setReferralMessage("Your referral link is temporarily unavailable.");
        }
      })
      .finally(() => {
        if (active) setIsReferralLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const showSpmComingSoon = false;
  const showComingSoon = showSpmComingSoon && examType === "SPM";

  const uecRegisteredSubjectValueSet = useMemo(
    () => new Set(getRegisteredSubjectValues(user?.tuition?.subject)),
    [user?.tuition?.subject],
  );
  const spmRegisteredSubjectSet = useMemo(() => {
    const subjects = Array.isArray(user?.SpmTuition?.subject)
      ? user.SpmTuition.subject
      : [];

    return new Set(subjects.map(toSpmSubjectKey).filter(Boolean));
  }, [user?.SpmTuition?.subject]);
  const hasUecQualification =
    user?.tuition?.qualification?.trim().toUpperCase() === "UEC";
  const hasSpmQualification =
    user?.SpmTuition?.qualification?.trim().toUpperCase() === "SPM";
  const examData = EXAM_HUB_DATA[examType];
  const { sessions, resources: notes, teachers } = examData;
  const subjectFilters = useMemo(
    () => [
      {
        id: "all",
        name: "All Subjects",
        color: "#0F172A",
      },
      ...examData.subjects,
    ],
    [examData.subjects],
  );

  const filteredSessions = useMemo(
    () =>
      sessions.filter((session) => {
        const matchSubject =
          subjectFilter === "all" || session.subjectId === subjectFilter;
        const matchStatus =
          sessionFilter === "all" || session.status === sessionFilter;
        return matchSubject && matchStatus;
      }),
    [sessions, subjectFilter, sessionFilter],
  );

  const normalizedNoteSearch = useMemo(
    () => noteSearch.trim().toLowerCase(),
    [noteSearch],
  );
  const filteredNotes = useMemo(
    () =>
      notes.filter((note) => {
        const matchSubject =
          subjectFilter === "all" || note.subjectId === subjectFilter;
        const matchType =
          noteTypeFilter === "all" || note.type === noteTypeFilter;
        const matchSearch =
          !normalizedNoteSearch ||
          note.title.toLowerCase().includes(normalizedNoteSearch) ||
          note.subject.toLowerCase().includes(normalizedNoteSearch);
        return matchSubject && matchType && matchSearch;
      }),
    [notes, subjectFilter, noteTypeFilter, normalizedNoteSearch],
  );

  const upcomingCount = useMemo(
    () => sessions.filter((session) => session.status === "upcoming").length,
    [sessions],
  );
  const recordingCount = useMemo(
    () => sessions.filter((session) => session.status === "recordings").length,
    [sessions],
  );
  const teacherCount = useMemo(
    () =>
      new Set(sessions.map((session) => session.teacherId).filter(Boolean))
        .size,
    [sessions],
  );
  const teacherById = useMemo(
    () => new Map(teachers.map((teacher) => [teacher.id, teacher])),
    [teachers],
  );

  const handleExamTypeChange = (nextExamType: ExamType) => {
    setTab("schedule");
    setSubjectFilter("all");
    setSessionFilter("all");
    setNoteTypeFilter("all");
    setNoteSearch("");
    setExamType(nextExamType);
  };

  const handleShareReferralLink = async () => {
    if (!referralCode) return;

    const path =
      examType === "UEC"
        ? "/uec-ultimate-revision-class"
        : "/spm-ultimate-revision-class";
    const referralUrl = new URL(path, window.location.origin);
    referralUrl.searchParams.set("ref", referralCode);
    const url = referralUrl.toString();

    try {
      await navigator.clipboard.writeText(url);
      setReferralMessage("Link copied");
    } catch (error) {
      console.error("Failed to copy referral link:", error);
      setReferralMessage("Unable to copy the link. Please try again.");
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center shadow-md">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1
              className="text-[24px] lg:text-[30px] text-slate-900 tracking-tight"
              style={{ fontWeight: 900 }}
            >
              Exam Hub
            </h1>
            <p
              className="text-[13px] text-slate-500"
              style={{ fontWeight: 400 }}
            >
              Your Ultimate Revision Class schedule and study resources.
            </p>
          </div>
        </div>

        <div className="w-full flex items-center justify-between px-6 py-5 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white">
          <div>
            <p className="font-semibold">
              🎁 Refer your friends and get rewards!
            </p>
            <p className="text-sm">
              Share your link and earn rewards when friends join.
            </p>
          </div>

          <button
            type="button"
            onClick={handleShareReferralLink}
            disabled={isReferralLoading || !referralCode}
            className="flex items-center text-sm gap-2 px-5 py-2 rounded-lg bg-white/15 hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60 text-white font-semibold transition-colors cursor-pointer"
          >
            {isReferralLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : referralMessage.includes("copied") ||
              referralMessage.includes("shared") ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {isReferralLoading
              ? "Preparing Link"
              : referralMessage === "Link copied"
                ? "Link copied"
                : `Share ${examType} Link`}
          </button>
        </div>
        {referralMessage && (
          <p className="mt-2 text-right text-[12px] text-slate-500" role="status">
            {referralMessage}
          </p>
        )}

        {/* Exam tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl mt-5">
          {Object.values(EXAM_HUB_DATA).map((exam) => (
            <button
              key={exam.id}
              onClick={() => handleExamTypeChange(exam.id)}
              className={`flex-1 py-2.5 rounded-xl text-[13px] transition-all cursor-pointer ${
                examType === exam.id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              style={{ fontWeight: examType === exam.id ? 700 : 500 }}
            >
              {exam.label}
            </button>
          ))}
        </div>

        {showComingSoon && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 rounded-3xl border border-violet-100 bg-gradient-to-br from-violet-50 via-white to-purple-50 px-6 py-16 text-center shadow-sm"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-md">
              <GraduationCap className="h-7 w-7" />
            </div>
            <h2
              className="text-2xl tracking-tight text-slate-900"
              style={{ fontWeight: 900 }}
            >
              Coming Soon
            </h2>
            <p className="mx-auto mt-2 max-w-md text-[14px] leading-6 text-slate-500">
              SPM schedules and study resources are being prepared. Check back
              soon for updates.
            </p>
          </motion.div>
        )}

        {/* Stats strip */}
        {!showComingSoon && (
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[
              // {
              //   label:
              //     examType === "SPM" ? "Recorded Classes" : "Upcoming Classes",
              //   value: (examType === "SPM"
              //     ? recordingCount
              //     : upcomingCount
              //   ).toString(),
              //   color: "from-violet-600 to-purple-600",
              //   icon: <Video className="w-4 h-4" />,
              // },
              {
                label: "Recorded Classes",
                value: recordingCount.toString(),
                color: "from-violet-600 to-purple-600",
                icon: <Video className="w-4 h-4" />,
              },
              {
                label: "Study Resources",
                value: notes.length.toString(),
                color: "from-pink-500 to-rose-500",
                icon: <BookOpen className="w-4 h-4" />,
              },
              {
                label: "Expert Teachers",
                value: teacherCount.toString(),
                color: "from-orange-500 to-amber-400",
                icon: <Star className="w-4 h-4" />,
              },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3"
              >
                <div
                  className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-md flex-shrink-0`}
                >
                  {s.icon}
                </div>
                <div>
                  <p
                    className="text-[20px] text-slate-900 leading-none"
                    style={{ fontWeight: 900 }}
                  >
                    {s.value}
                  </p>
                  <p
                    className="text-[11px] text-slate-500 mt-0.5"
                    style={{ fontWeight: 500 }}
                  >
                    {s.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {!showComingSoon && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl mb-6">
            {(
              [
                // {
                //   key: "schedule",
                //   label:
                //     examType === "SPM"
                //       ? "Tuition Recordings"
                //       : "Tuition Schedule",
                //   icon: <CalendarDays className="w-4 h-4" />,
                // },
                {
                  key: "schedule",
                  label: "Tuition Recordings",
                  icon: <CalendarDays className="w-4 h-4" />,
                },
                {
                  key: "resources",
                  label: "Study Resources",
                  icon: <BookOpen className="w-4 h-4" />,
                },
              ] as { key: Tab; label: string; icon: React.ReactNode }[]
            ).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] transition-all ${
                  tab === t.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700 cursor-pointer"
                }`}
                style={{ fontWeight: tab === t.key ? 700 : 500 }}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Subject filter — no animation */}
          <div className="flex gap-2 overflow-x-auto pb-1 mb-5 no-scrollbar">
            {subjectFilters.map((s) => {
              const isSelected = subjectFilter === s.id;
              const subjectColor = s.color || "#64748B";

              return (
                <button
                  key={s.id}
                  onClick={() => setSubjectFilter(s.id)}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[12px] border transition-colors cursor-pointer ${
                    isSelected
                      ? "text-white"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  style={{
                    fontWeight: isSelected ? 700 : 500,
                    ...(isSelected
                      ? {
                          backgroundColor: subjectColor,
                          borderColor: subjectColor,
                        }
                      : {}),
                  }}
                >
                  {s.name}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {/* ── Schedule Tab ── */}
            {tab === "schedule" && (
              <motion.div
                key="schedule"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex gap-2 mb-5">
                  {(
                    ["all", "upcoming", "completed", "recordings"] as const
                  ).map((f) => (
                    <button
                      key={f}
                      onClick={() => setSessionFilter(f)}
                      className={`px-4 py-1.5 rounded-xl text-[12px] border transition-colors cursor-pointer ${
                        sessionFilter === f
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                      }`}
                      style={{ fontWeight: sessionFilter === f ? 700 : 500 }}
                    >
                      {f === "all"
                        ? "All Classes"
                        : f === "upcoming"
                          ? `Upcoming (${upcomingCount})`
                          : f === "completed"
                            ? "Completed"
                            : f === "recordings"
                              ? `Recordings (${recordingCount})`
                              : ""}
                    </button>
                  ))}
                </div>

                {filteredSessions.length === 0 ? (
                  <div className="text-center py-16 text-slate-400">
                    <CalendarDays className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <p className="text-[14px]" style={{ fontWeight: 500 }}>
                      No classes found for this filter.
                    </p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4 items-stretch">
                    {filteredSessions.map((session) => {
                      const canAccess =
                        examType === "SPM"
                          ? spmRegisteredSubjectSet.has(
                              toSpmSubjectKey(session.subject),
                            )
                          : uecRegisteredSubjectValueSet.has(
                              getUecSubjectValue(session.subject),
                            );
                      const teacher = teacherById.get(session.teacherId);
                      return (
                        <SessionCard
                          key={session.id}
                          session={session}
                          teacher={teacher}
                          canAccess={canAccess}
                        />
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── Resources Tab ── */}
            {tab === "resources" && (
              <motion.div
                key="resources"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex flex-col sm:flex-row gap-3 mb-5">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      value={noteSearch}
                      onChange={(e) => setNoteSearch(e.target.value)}
                      placeholder="Search resources..."
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-700 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                      style={{ fontWeight: 400 }}
                    />
                  </div>
                  <div className="flex gap-2">
                    {[
                      { key: "all", label: "All" },
                      { key: "schedule", label: "Schedule" },
                      { key: "notes", label: "Notes" },
                      { key: "past-paper", label: "Past Papers" },
                      { key: "formula-sheet", label: "Assignments" },
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setNoteTypeFilter(f.key)}
                        className={`px-3.5 py-2 rounded-xl text-[12px] border transition-colors cursor-pointer ${
                          noteTypeFilter === f.key
                            ? "bg-violet-600 text-white border-violet-600"
                            : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                        }`}
                        style={{
                          fontWeight: noteTypeFilter === f.key ? 700 : 500,
                        }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredNotes.length === 0 ? (
                  <div className="text-center py-16 text-slate-400">
                    <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <p className="text-[14px]" style={{ fontWeight: 500 }}>
                      No resources found.
                    </p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                    {filteredNotes.map((note) => {
                      const canAccess =
                        note.id === "uec_schedule"
                          ? hasUecQualification
                          : note.id === "spm_schedule"
                            ? hasSpmQualification
                            : note.public === true ||
                              (examType === "SPM"
                                ? spmRegisteredSubjectSet.has(
                                    toSpmSubjectKey(note.subject),
                                  )
                                : uecRegisteredSubjectValueSet.has(
                                    getUecSubjectValue(note.subject),
                                  ));
                      return (
                        <ResourceCard
                          key={note.id}
                          note={note}
                          canAccess={canAccess}
                        />
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
